import styled, { css } from 'styled-components';
import {
  STAT_TILE_ALIGN_START,
  STAT_TILE_LABEL_TRANSFORM_UPPERCASE,
  STAT_TILE_SIZE_TOKENS,
  STAT_TILE_TONE_DEFAULT,
  type StatTileAlign,
  type StatTileLabelTransform,
  type StatTileSize,
  type StatTileTone,
} from './StatTile.constants';

interface RootProps {
  $align: StatTileAlign;
  $size: StatTileSize;
}

const TONE_COLOR: Record<
  StatTileTone,
  (theme: { colors?: Record<string, string | undefined> }) => string
> = {
  default: ({ colors = {} }) => colors.onSurface ?? '#E4E1ED',
  primary: ({ colors = {} }) => colors.primary ?? '#C1C1FF',
  success: ({ colors = {} }) => colors.success ?? '#8CD88C',
  caution: ({ colors = {} }) => colors.caution ?? '#FFDF9E',
  error: ({ colors = {} }) => colors.error ?? '#FFB4AB',
  // `info` prefers a dedicated `colors.info` when the consumer's theme
  // exposes one, falling back to `primary` (most M3 themes don't include
  // an `info` key) and finally to a literal so it still renders.
  info: ({ colors = {} }) => colors.info ?? colors.primary ?? '#C1C1FF',
};

const resolveToneColor = (
  tone: StatTileTone,
  theme: { colors?: Record<string, string | undefined> },
): string => (TONE_COLOR[tone] ?? TONE_COLOR[STAT_TILE_TONE_DEFAULT])(theme);

export const Root = styled.div<RootProps>`
  display: inline-flex;
  flex-direction: column;
  min-width: 0;
  ${({ $align }) =>
    $align === STAT_TILE_ALIGN_START
      ? css`
          align-items: flex-start;
          text-align: left;
        `
      : css`
          align-items: center;
          text-align: center;
        `}
  ${({ $size }) => css`
    gap: ${STAT_TILE_SIZE_TOKENS[$size].gap};
  `}
`;

export const Value = styled.div<{ $size: StatTileSize; $tone: StatTileTone }>`
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  ${({ $size }) => {
    const tokens = STAT_TILE_SIZE_TOKENS[$size];
    return css`
      font-size: ${tokens.valueFontSize};
      line-height: ${tokens.valueLineHeight};
      font-weight: ${tokens.valueFontWeight};
      letter-spacing: ${tokens.valueLetterSpacing};
    `;
  }}
  ${({ $tone, theme }) => css`
    color: ${resolveToneColor($tone, theme)};
  `}
`;

export const Label = styled.div<{
  $size: StatTileSize;
  $transform: StatTileLabelTransform;
}>`
  font-weight: 500;
  text-transform: ${({ $transform }) =>
    $transform === STAT_TILE_LABEL_TRANSFORM_UPPERCASE ? 'uppercase' : 'none'};
  ${({ $size }) => {
    const tokens = STAT_TILE_SIZE_TOKENS[$size];
    return css`
      font-size: ${tokens.labelFontSize};
      letter-spacing: ${tokens.labelLetterSpacing};
    `;
  }}
  ${({ theme: { colors = {} } }) => css`
    color: ${colors.onSurfaceVariant ?? '#C7C4D7'};
  `}
`;
